import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ShieldCheck } from 'lucide-react';
import { formatDate } from '@/shared/lib/format';
import type { Answer } from '../api/schemas';
import { AnswerFeedback } from '@/features/feedback';
import { AnswerActions } from './AnswerActions';
import { AnswerText } from './AnswerText';
import { CitationList } from './CitationList';
import { riseSx } from './motion';
import { AnswerPanels } from './AnswerPanels';
import { ChangeStrip } from './ChangeStrip';
import { RefusalBody } from './RefusalBody';

type Props = {
  answer: Answer;
  onAsk?: ((question: string) => void) | undefined;
  question?: string;
  context?: string[];
  deskQuestionId?: string | undefined;
  onDeskAsked?: ((questionId: string) => void) | undefined;
};

export function AnswerBody({ answer, onAsk, question = '', context = [], deskQuestionId, onDeskAsked }: Props) {
  if (answer.status === 'refused') {
    return <RefusalBody answer={answer} onAsk={onAsk} question={question} context={context} deskQuestionId={deskQuestionId} onDeskAsked={onDeskAsked} />;
  }
  const change = answer.viewChanged;
  return (
    <Stack sx={{ gap: 2 }}>
      <AnswerText text={answer.text} citations={answer.citations} />
      {/* The text is already on screen from streaming; the rest arrives with the finished answer. */}
      <Stack sx={{ gap: 2, ...riseSx }}>
        {change ? <ChangeStrip change={change} /> : null}
        <AnswerPanels panels={answer.panels} citations={answer.citations} />
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <CitationList citations={answer.citations} />
          {answer.verified && answer.citations.length > 0 ? (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, color: 'success.dark' }}>
              <ShieldCheck size={13} aria-hidden />
              <Typography variant="caption">Every claim checked against its source</Typography>
            </Stack>
          ) : null}
        </Stack>
        {answer.route === 'chat' ? null : (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap', borderTop: 1, borderColor: 'divider', pt: 1 }}>
            <AnswerActions answer={answer} />
            <AnswerFeedback about={{ question, answer: answer.text, route: answer.route, citations: answer.citations }} />
            {answer.asOf ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', ml: { sm: 'auto' } }}>As of {formatDate(answer.asOf)}</Typography>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
